import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlayerType } from '../../../models/video';
import PlayerSambatech from '../../atoms/PlayerSambatech';
import PlayerVimeo from '../../atoms/PlayerVimeo';
import PlayerYoutube from '../../atoms/PlayerYoutube';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import PlayerWatermark from './PlayerWatermark';

/** Duração da marca d'água a partir do início da reprodução (ms). */
const WATERMARK_DURATION_MS = 60000;

//Se alterar alguma prop, lembrar de mudar a função de memoização
interface PlayerProps {
  playerType: PlayerType;
  token: string;
  initialTime: number;
  seekTo?: { seconds: number; requestId: number };
  onFinish?: () => any;
  virtualFinishAt?: number;
  onVideoEnd?: () => void;
  /** Segundos assistidos. Repassado aos players pelo spread de props. */
  onProgressSeconds?: (seconds: number) => void;
  className?: string;
  contentId?: string;
  captionUri?: string;
}

const VideoPlayer: React.FC<PlayerProps> = (props) => {
  const { playerType = 'sambatech' } = props;
  const user = useUserStore().user;

  const videoQuality = user?.videoQuality || '360';
  const videoSpeed = user?.videoPlaybackSpeed || '1';

  // Marca d'água: a cada início de reprodução aparece e dura os primeiros 60 segundos,
  // depois some até o próximo play.
  const [showWatermark, setShowWatermark] = useState(false);
  const watermarkTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const handleStart = useCallback(() => {
    setShowWatermark(true);
    if (watermarkTimeoutRef.current) {
      clearTimeout(watermarkTimeoutRef.current);
    }
    watermarkTimeoutRef.current = setTimeout(
      () => setShowWatermark(false),
      WATERMARK_DURATION_MS
    );
  }, []);

  useEffect(() => {
    return () => {
      if (watermarkTimeoutRef.current) {
        clearTimeout(watermarkTimeoutRef.current);
      }
    };
  }, []);

  const playerRenderers = {
    sambatech: () => (
      <PlayerSambatech
        {...props}
        virtualFinishAt={props.virtualFinishAt}
        onStart={handleStart}
        onResume={handleStart}
      />
    ),
    samba: () => (
      <PlayerSambatech
        {...props}
        virtualFinishAt={props.virtualFinishAt}
        onStart={handleStart}
        onResume={handleStart}
      />
    ),
    vimeo: () => (
      <PlayerVimeo
        {...props}
        virtualFinishAt={props.virtualFinishAt}
        videoQuality={videoQuality}
        videoSpeed={videoSpeed}
        onStart={handleStart}
        onPlay={handleStart}
      />
    ),
    youtube: () => (
      <PlayerYoutube {...props} onStart={handleStart} onPlay={handleStart} />
    ),
  };

  const renderPlayer = () => {
    const renderer = playerRenderers[playerType];
    return renderer ? (
      <>
        <div {...(props.captionUri ? { 'data-content-id': props.contentId } : {})} />
        {renderer()}
      </>
    ) : (
      <div>Player não suportado</div>
    );
  };

  return (
    <div className={`relative h-full w-full ${props.className ?? ''}`}>
      {renderPlayer()}
      {showWatermark && (
        <PlayerWatermark contentId={props.contentId} studentId={user?.id} />
      )}
    </div>
  );
};

const memoizedVideoPlayer = React.memo(VideoPlayer, (prevProps, nextProps) => {
  return (
    prevProps.playerType === nextProps.playerType &&
    prevProps.token === nextProps.token &&
    prevProps.initialTime === nextProps.initialTime &&
    prevProps.seekTo?.requestId === nextProps.seekTo?.requestId &&
      prevProps.captionUri === nextProps.captionUri &&
    prevProps.seekTo?.seconds === nextProps.seekTo?.seconds &&
    prevProps.className === nextProps.className &&
    prevProps.virtualFinishAt === nextProps.virtualFinishAt
  );
});

export default memoizedVideoPlayer;
